/**
 * ERDiagramPanel — interactive entity-relationship diagram for a database.
 *
 * Fetches the whole-database table + FK graph (/api/.../er-graph) in one call,
 * auto-lays it out (force-directed, web/src/lib/erlayout.js), and renders table
 * cards (HTML) + foreign-key edges (SVG) inside a pan/zoom "world". Drag cards to
 * rearrange (persisted per connection+database in localStorage), click a table to
 * focus its relationships, collapse cards to headers, and export to SVG/PNG.
 *
 * Rendering model: one transformed container holds an SVG edge layer + absolutely
 * positioned card divs, so edges and cards share the same world coordinates.
 */
import { createSignal, createMemo, createResource, createEffect, on, onCleanup, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { api } from '../lib/api.js';
import { fetchERGraph } from '../lib/erdiagram.js';
import { layoutNodes } from '../lib/erlayout.js';
import { toast } from '../lib/toast.js';

const HEADER_H = 30;
const ROW_H = 20;
const CHAR_W = 7;       // approx mono char width at 12px
const MIN_W = 172, MAX_W = 360;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const shortType = (t) => (t || '').replace(/\s+unsigned/i, ' u').replace(/\s+zerofill/i, '');

// Card size from its columns (exact, so layout matches the rendered card).
const tableW = (t) => {
  let max = t.name.length + 5;
  for (const c of t.columns) max = Math.max(max, c.name.length + 3 + Math.min(shortType(c.type).length, 16));
  return clamp(Math.round(max * CHAR_W) + 22, MIN_W, MAX_W);
};
const tableH = (t, collapsed) => (collapsed ? HEADER_H : HEADER_H + t.columns.length * ROW_H + 6);

export default function ERDiagramPanel(props) {
  // ── connection + database selection ────────────────────────────────────────
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal(props.connectionId ? String(props.connectionId) : '');
  const [dbList, setDbList] = createSignal(null);
  const [database, setDatabase] = createSignal(props.database || '');

  const loadDbs = async (id) => {
    if (!id) { setDbList(null); return; }
    try {
      const r = await api.get(`/api/connections/${id}/databases`);
      setDbList((r.databases || []).map((d) => d.name));
    } catch { setDbList([]); }
  };
  createEffect(() => { if (connId()) loadDbs(connId()); });

  // ── graph data ─────────────────────────────────────────────────────────────
  const graphKey = () => (connId() && database() ? { id: connId(), db: database() } : null);
  const [graph, { refetch }] = createResource(graphKey, (k) => fetchERGraph(k.id, k.db));

  const tables = () => graph()?.tables || [];
  const edges = () => graph()?.edges || [];
  const tableByName = createMemo(() => {
    const m = new Map();
    for (const t of tables()) m.set(t.name, t);
    return m;
  });

  // ── view state ─────────────────────────────────────────────────────────────
  const [pos, setPos] = createStore({});          // table name -> {x,y}
  const [collapsed, setCollapsed] = createSignal(new Set());
  const [selected, setSelected] = createSignal(null);
  const [search, setSearch] = createSignal('');
  const [zoom, setZoom] = createSignal(1);
  const [panX, setPanX] = createSignal(0);
  const [panY, setPanY] = createSignal(0);
  let viewportEl;

  const sizes = createMemo(() => {
    const col = collapsed();
    const m = new Map();
    for (const t of tables()) m.set(t.name, { w: tableW(t), h: tableH(t, col.has(t.name)) });
    return m;
  });

  // v2: invalidates layouts saved by the pre-frame-clamp algorithm (which spread
  // tables over a huge area), so existing diagrams re-lay-out cleanly once.
  const storeKey = () => `ninocat:er:v2:${connId()}:${database()}`;
  const savePositions = () => {
    try { localStorage.setItem(storeKey(), JSON.stringify(pos)); } catch { /* quota — ignore */ }
  };
  const buildNodes = () => tables().map((t) => ({ id: t.name, ...sizes().get(t.name) }));

  const runLayout = (save = true) => {
    const p = layoutNodes(buildNodes(), edges().map((e) => ({ source: e.fromTable, target: e.toTable })));
    setPos(reconcile(p));
    if (save) savePositions();
    queueMicrotask(fit);
  };

  // On new graph ONLY (on(graph,…) so collapsing/resizing cards never re-runs this
  // and clobbers a hand-arranged layout): restore saved positions if they cover
  // every table, else auto-lay-out.
  createEffect(on(graph, (g) => {
    if (!g) return;
    const names = (g.tables || []).map((t) => t.name);
    if (names.length === 0) { setPos(reconcile({})); return; }
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(storeKey()) || 'null'); } catch { saved = null; }
    if (saved && names.every((n) => saved[n])) {
      setPos(reconcile(saved));
      queueMicrotask(fit);
    } else {
      runLayout(true);
    }
    setSelected(null);
  }));

  // ── content bounds + fit ─────────────────────────────────────────────────────
  const bounds = createMemo(() => {
    let maxX = 0, maxY = 0;
    for (const t of tables()) {
      const p = pos[t.name]; if (!p) continue;
      const s = sizes().get(t.name);
      maxX = Math.max(maxX, p.x + s.w); maxY = Math.max(maxY, p.y + s.h);
    }
    return { w: maxX + 60, h: maxY + 60 };
  });

  const fit = () => {
    if (!viewportEl) return;
    const b = bounds();
    const vw = viewportEl.clientWidth, vh = viewportEl.clientHeight;
    if (b.w <= 60 || vw === 0) return;
    const z = clamp(Math.min(vw / b.w, vh / b.h, 1), 0.15, 1);
    setZoom(z);
    setPanX((vw - b.w * z) / 2);
    setPanY(Math.max(12, (vh - b.h * z) / 2));
  };

  // ── pan / zoom ───────────────────────────────────────────────────────────────
  const onWheel = (e) => {
    e.preventDefault();
    const rect = viewportEl.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const wx = (sx - panX()) / zoom(), wy = (sy - panY()) / zoom();
    const nz = clamp(zoom() * (e.deltaY < 0 ? 1.12 : 1 / 1.12), 0.15, 2.5);
    setPanX(sx - wx * nz); setPanY(sy - wy * nz); setZoom(nz);
  };
  const zoomBy = (f) => {
    const vw = viewportEl.clientWidth / 2, vh = viewportEl.clientHeight / 2;
    const wx = (vw - panX()) / zoom(), wy = (vh - panY()) / zoom();
    const nz = clamp(zoom() * f, 0.15, 2.5);
    setPanX(vw - wx * nz); setPanY(vh - wy * nz); setZoom(nz);
  };

  let panStart = null;
  const onViewportPointerDown = (e) => {
    if (e.target.closest('.er-card')) return; // a card handles its own drag
    panStart = { x: e.clientX, y: e.clientY, px: panX(), py: panY() };
    setSelected(null);
    viewportEl.setPointerCapture?.(e.pointerId);
  };
  const onViewportPointerMove = (e) => {
    if (!panStart) return;
    setPanX(panStart.px + (e.clientX - panStart.x));
    setPanY(panStart.py + (e.clientY - panStart.y));
  };
  const endPan = () => { panStart = null; };

  // ── card drag ────────────────────────────────────────────────────────────────
  let drag = null;
  const onCardPointerDown = (e, name) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const p = pos[name] || { x: 0, y: 0 };
    drag = { name, sx: e.clientX, sy: e.clientY, ox: p.x, oy: p.y, moved: false };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragUp);
  };
  const onDragMove = (e) => {
    if (!drag) return;
    const dx = (e.clientX - drag.sx) / zoom(), dy = (e.clientY - drag.sy) / zoom();
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) drag.moved = true;
    setPos(drag.name, { x: Math.round(drag.ox + dx), y: Math.round(drag.oy + dy) });
  };
  const onDragUp = () => {
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragUp);
    if (drag?.moved) savePositions();
    const d = drag; drag = null;
    // A click without movement selects the table.
    if (d && !d.moved) setSelected(selected() === d.name ? null : d.name);
  };
  onCleanup(() => { window.removeEventListener('pointermove', onDragMove); window.removeEventListener('pointerup', onDragUp); });

  // ── selection / focus ─────────────────────────────────────────────────────────
  const neighbors = createMemo(() => {
    const sel = selected();
    if (!sel) return null;
    const set = new Set([sel]);
    for (const e of edges()) {
      if (e.fromTable === sel) set.add(e.toTable);
      if (e.toTable === sel) set.add(e.fromTable);
    }
    return set;
  });
  const searchHit = (name) => {
    const q = search().trim().toLowerCase();
    return q ? name.toLowerCase().includes(q) : false;
  };

  // ── edge geometry ─────────────────────────────────────────────────────────────
  // Anchor on the column row (when expanded) and the side facing the other card.
  const colYOf = (t, name, colName, isCollapsed) => {
    const p = pos[name]; if (!p) return 0;
    if (isCollapsed) return p.y + HEADER_H / 2;
    const idx = t.columns.findIndex((c) => c.name === colName);
    if (idx < 0) return p.y + HEADER_H / 2;
    return p.y + HEADER_H + idx * ROW_H + ROW_H / 2;
  };
  const edgeGeom = (e) => {
    const sp = pos[e.fromTable], tp = pos[e.toTable];
    const st = tableByName().get(e.fromTable), tt = tableByName().get(e.toTable);
    if (!sp || !tp || !st || !tt) return null;
    const ss = sizes().get(e.fromTable), ts = sizes().get(e.toTable);
    const col = collapsed();
    const sCol = col.has(e.fromTable), tCol = col.has(e.toTable);

    // Self-reference: a small loop off the right edge.
    if (e.fromTable === e.toTable) {
      const y1 = colYOf(st, e.fromTable, e.fromCols[0], sCol);
      const y2 = colYOf(st, e.fromTable, e.toCols[0], sCol);
      const x = sp.x + ss.w;
      const out = x + 46;
      return { path: `M ${x} ${y1} C ${out} ${y1}, ${out} ${y2}, ${x} ${y2}`, tx: x, ty: y2, tRight: true };
    }

    const scx = sp.x + ss.w / 2, tcx = tp.x + ts.w / 2;
    const sRight = tcx >= scx;
    const sx = sp.x + (sRight ? ss.w : 0);
    const sy = colYOf(st, e.fromTable, e.fromCols[0], sCol);
    const tRight = scx > tcx; // enter target on the side facing the source
    const tx = tp.x + (tRight ? ts.w : 0);
    const ty = colYOf(tt, e.toTable, e.toCols[0], tCol);

    const d = Math.max(40, Math.abs(tx - sx) * 0.45);
    const c1x = sx + (sRight ? d : -d);
    const c2x = tx + (tRight ? d : -d);
    return { path: `M ${sx} ${sy} C ${c1x} ${sy}, ${c2x} ${ty}, ${tx} ${ty}`, tx, ty, tRight };
  };

  // ── export ─────────────────────────────────────────────────────────────────────
  const exportSVG = () => download(buildSVG(), `${database() || 'schema'}-er.svg`, 'image/svg+xml');
  const exportPNG = () => {
    const svg = buildSVG();
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const cv = document.createElement('canvas');
      cv.width = bounds().w * scale; cv.height = bounds().h * scale;
      const ctx = cv.getContext('2d');
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      cv.toBlob((b) => { if (b) download(b, `${database() || 'schema'}-er.png`); }, 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); toast.error('PNG export failed'); };
    img.src = url;
  };

  // buildSVG renders a standalone, print-friendly (light) SVG of the current
  // layout — independent of the interactive DOM so it rasterizes cleanly.
  const buildSVG = () => {
    const b = bounds();
    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const parts = [];
    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${b.w}" height="${b.h}" viewBox="0 0 ${b.w} ${b.h}" font-family="ui-sans-serif,system-ui,sans-serif">`);
    parts.push(`<defs><marker id="ar" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto"><path d="M0,0 L7,3 L0,6 Z" fill="#64748b"/></marker></defs>`);
    parts.push(`<rect width="${b.w}" height="${b.h}" fill="#ffffff"/>`);
    // edges first (under cards)
    for (const e of edges()) {
      const g = edgeGeom(e); if (!g) continue;
      parts.push(`<path d="${g.path}" fill="none" stroke="#94a3b8" stroke-width="1.5" marker-end="url(#ar)"/>`);
    }
    // cards
    for (const t of tables()) {
      const p = pos[t.name]; if (!p) continue;
      const w = tableW(t), col = collapsed().has(t.name), h = tableH(t, col);
      parts.push(`<g transform="translate(${p.x},${p.y})">`);
      parts.push(`<rect width="${w}" height="${h}" rx="7" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.2"/>`);
      parts.push(`<path d="M0,7 a7,7 0 0 1 7,-7 h${w - 14} a7,7 0 0 1 7,7 v${HEADER_H - 7} h-${w} Z" fill="#0ea5e9"/>`);
      parts.push(`<text x="9" y="${HEADER_H / 2 + 4}" fill="#ffffff" font-size="12.5" font-weight="700">${esc(t.name)}</text>`);
      if (!col) {
        t.columns.forEach((c, i) => {
          const y = HEADER_H + i * ROW_H + ROW_H / 2 + 4;
          const mark = c.pk ? '🔑 ' : c.fk ? '→ ' : '';
          parts.push(`<text x="9" y="${y}" fill="#0f172a" font-size="11" font-family="ui-monospace,monospace"${c.pk ? ' font-weight="700"' : ''}>${esc(mark + c.name)}</text>`);
          parts.push(`<text x="${w - 9}" y="${y}" fill="#64748b" font-size="10" text-anchor="end" font-family="ui-monospace,monospace">${esc(shortType(c.type))}</text>`);
        });
      }
      parts.push(`</g>`);
    }
    parts.push(`</svg>`);
    return parts.join('');
  };

  const collapseAll = (v) => {
    setCollapsed(v ? new Set(tables().map((t) => t.name)) : new Set());
    queueMicrotask(fit);
  };
  const toggleCollapse = (name) => {
    const n = new Set(collapsed());
    n.has(name) ? n.delete(name) : n.add(name);
    setCollapsed(n);
  };

  const [exportOpen, setExportOpen] = createSignal(false);

  return (
    <div class="er-panel">
      {/* Toolbar */}
      <div class="er-toolbar">
        <select class="input er-sel" value={connId()} onChange={(e) => { setConnId(e.currentTarget.value); setDatabase(''); }}>
          <option value="">— connection —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
        <select class="input er-sel" value={database()} onChange={(e) => setDatabase(e.currentTarget.value)} disabled={!dbList()}>
          <option value="">— database —</option>
          <For each={dbList() || []}>{(n) => <option value={n}>{n}</option>}</For>
        </select>

        <Show when={graph()}>
          <span class="er-divider" />
          <button class="btn btn-sm btn-ghost" title="Re-run auto-layout" onClick={() => runLayout(true)}>✦ Auto-layout</button>
          <button class="btn btn-sm btn-ghost" title="Fit to view" onClick={fit}>⤢ Fit</button>
          <div class="er-zoom">
            <button class="btn btn-sm btn-ghost" onClick={() => zoomBy(1 / 1.2)} title="Zoom out">−</button>
            <span class="er-zoom-val">{Math.round(zoom() * 100)}%</span>
            <button class="btn btn-sm btn-ghost" onClick={() => zoomBy(1.2)} title="Zoom in">+</button>
          </div>
          <button class="btn btn-sm btn-ghost" onClick={() => collapseAll(true)} title="Collapse all to headers">⊟ Collapse</button>
          <button class="btn btn-sm btn-ghost" onClick={() => collapseAll(false)} title="Expand all">⊞ Expand</button>
          <div class="export-menu">
            <button class="btn btn-sm btn-ghost" onClick={() => setExportOpen(!exportOpen())}>⤓ Export ▾</button>
            <Show when={exportOpen()}>
              <div class="export-menu-pop" onMouseLeave={() => setExportOpen(false)}>
                <button class="export-menu-item" onClick={() => { setExportOpen(false); exportPNG(); }}>PNG (image)</button>
                <button class="export-menu-item" onClick={() => { setExportOpen(false); exportSVG(); }}>SVG (vector)</button>
              </div>
            </Show>
          </div>
          <span class="spacer" />
          <input class="input er-search" placeholder="Find table…" value={search()}
                 onInput={(e) => setSearch(e.currentTarget.value)} />
          <span class="muted er-count">{tables().length} tables · {edges().length} FKs</span>
        </Show>
      </div>

      {/* Canvas */}
      <div class="er-viewport" ref={viewportEl}
           onWheel={onWheel}
           onPointerDown={onViewportPointerDown} onPointerMove={onViewportPointerMove}
           onPointerUp={endPan} onPointerLeave={endPan}>
        <Show when={!graphKey()}>
          <div class="er-empty">Pick a connection and database to draw its ER diagram.</div>
        </Show>
        <Show when={graph.loading}><div class="er-empty">Loading schema…</div></Show>
        <Show when={graph.error}><div class="er-empty er-err">Couldn't load schema: {String(graph.error?.message || graph.error)}</div></Show>
        <Show when={graph() && tables().length === 0}><div class="er-empty">This database has no base tables.</div></Show>

        <Show when={graph() && tables().length > 0}>
          <div class="er-world" style={`transform:translate(${panX()}px,${panY()}px) scale(${zoom()});width:${bounds().w}px;height:${bounds().h}px;`}>
            {/* Edge layer */}
            <svg class="er-edges" width={bounds().w} height={bounds().h}>
              <For each={edges()}>
                {(e) => {
                  const g = () => edgeGeom(e);
                  const hl = () => selected() && (e.fromTable === selected() || e.toTable === selected());
                  const dim = () => selected() && !hl();
                  return (
                    <Show when={g()}>
                      <path d={g().path} class="er-edge" classList={{ 'er-edge-hl': !!hl(), 'er-edge-dim': !!dim() }}
                            marker-end={hl() ? 'url(#er-ar-hl)' : 'url(#er-ar)'} />
                    </Show>
                  );
                }}
              </For>
              <defs>
                <marker id="er-ar" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
                  <path d="M0,0 L7,3 L0,6 Z" class="er-ar" />
                </marker>
                <marker id="er-ar-hl" markerWidth="10" markerHeight="10" refX="7" refY="3" orient="auto">
                  <path d="M0,0 L7,3 L0,6 Z" class="er-ar-hl" />
                </marker>
              </defs>
            </svg>

            {/* Table cards */}
            <For each={tables()}>
              {(t) => {
                const p = () => pos[t.name] || { x: 0, y: 0 };
                const w = () => tableW(t);
                const isCol = () => collapsed().has(t.name);
                const nb = () => neighbors();
                const dim = () => nb() && !nb().has(t.name);
                return (
                  <div class="er-card"
                       classList={{ 'er-card-sel': selected() === t.name, 'er-card-dim': !!dim(), 'er-card-hit': searchHit(t.name) }}
                       style={`left:${p().x}px;top:${p().y}px;width:${w()}px;`}
                       onPointerDown={(e) => onCardPointerDown(e, t.name)}>
                    <div class="er-card-head" ondblclick={() => toggleCollapse(t.name)} title="Drag to move · click to focus · double-click to collapse">
                      <span class="er-card-name">{t.name}</span>
                      <Show when={t.rows > 0}><span class="er-card-rows">{t.rows.toLocaleString()}</span></Show>
                    </div>
                    <Show when={!isCol()}>
                      <div class="er-card-cols">
                        <For each={t.columns}>
                          {(c) => (
                            <div class="er-col" classList={{ 'er-col-pk': c.pk }}>
                              <span class="er-col-key">{c.pk ? '🔑' : c.fk ? '→' : ''}</span>
                              <span class="er-col-name">{c.name}</span>
                              <span class="er-col-type">{shortType(c.type)}{c.nullable ? '' : ' •'}</span>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
}

// download a Blob or string as a file.
function download(data, filename, mime) {
  const blob = typeof data === 'string' ? new Blob([data], { type: mime || 'text/plain' }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
